import { type ReactNode, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Pagination } from '@/components/Pagination'
import { Button } from '@/components/ui/button'
import { PreviewRow } from './PreviewRow'
import {
  countByFilter,
  FILTER_LABELS,
  FILTER_ORDER,
  type FieldColumn,
  filterOf,
  plannedCounts,
  type StatusFilter,
  undecided,
} from './rowChoices'
import type { ImportMode, PreviewRow as Verdict, RowChoice, RowField, RowInput } from './types'

const PAGE_SIZE = 50
// The header cells stick while the rows scroll; the line under them is a shadow, as in
// the dossier table: a collapsed border stays behind when the header sticks.
const HEAD = 'sticky top-0 z-10 bg-white py-2 shadow-[inset_0_-1px_0_var(--border)]'

interface PreviewGridProps {
  mode: ImportMode
  lead: FieldColumn
  others: readonly FieldColumn[]
  inputs: RowInput[]
  rows: Verdict[]
  choices: RowChoice[]
  checking: boolean
  committing: boolean
  /** The organisations panel, between the counts and the rows. */
  panel?: ReactNode
  /** The list the contacts of this import are added to (spec-63 E5); named in the question. */
  listName?: string
  onEdit: (index: number, field: RowField, value: string) => void
  onLeave: (index: number) => void
  onChoice: (index: number, choice: RowChoice) => void
  onAllNew: () => void
  onSkip: (indexes: number[]) => void
  onRecheckAll: () => void
  onBack: () => void
  onCommit: () => void
}

/** ③ Controleren (spec-63 E6): every cell editable, re-checked on the server per row. */
export function PreviewGrid(props: PreviewGridProps) {
  const { mode, lead, others, inputs, rows, choices, checking, committing } = props
  const [filter, setFilter] = useState<StatusFilter>('alle')
  const [requestedPage, setPage] = useState(1)
  const [selected, setSelected] = useState<ReadonlySet<number>>(new Set())
  const counts = useMemo(() => countByFilter(rows), [rows])
  const visible = useMemo(
    () => rows.flatMap((row, index) => (filter === 'alle' || filterOf(row.status) === filter ? [index] : [])),
    [rows, filter],
  )
  // A re-check can empty the last page of a filter (a fixed row leaves "Fout").
  const page = Math.min(requestedPage, Math.max(1, Math.ceil(visible.length / PAGE_SIZE)))
  const shown = visible.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
  const open = undecided(rows, choices)
  const planned = plannedCounts(rows, choices)
  const canCommit = !checking && !committing && open === 0 && planned.created + planned.known > 0
  const allShownSelected = shown.length > 0 && shown.every((index) => selected.has(index))
  // The rows scroll in a box of their own, so its sideways scrollbar is on screen; another
  // page or filter starts at its top.
  const scroller = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    if (scroller.current) scroller.current.scrollTop = 0
  }, [page, filter])

  function toggle(index: number, on: boolean) {
    setSelected((current) => {
      const next = new Set(current)
      if (on) next.add(index)
      else next.delete(index)
      return next
    })
  }

  function toggleShown(on: boolean) {
    setSelected((current) => {
      const next = new Set(current)
      for (const index of shown) {
        if (on) next.add(index)
        else next.delete(index)
      }
      return next
    })
  }

  /** A row set to merge with a row that will not be imported (skipped, or an error). */
  function targetSkipped(row: Verdict): boolean {
    const target = row.duplicate_of
    if (target === null) return false
    return choices[target]?.action === 'skip' || rows[target]?.status === 'fout'
  }

  const noun = mode === 'contacten' ? 'contacten' : 'organisaties'

  return (
    <section className="vonk-card flex flex-col gap-3 p-4" aria-labelledby="import-check">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="import-check" className="text-base font-semibold text-foreground">
          Controleren
        </h2>
        <p className="text-sm text-muted-foreground">
          {`${rows.length} rijen · ${counts.nieuw} nieuw · ${counts.bestaand} bestaand · ${counts.mogelijk} mogelijk dubbel · ${counts.founder} founder · ${counts.dubbel} dubbel in bestand · ${counts.fout} fout`}
        </p>
      </div>
      <div role="group" aria-label="Filter op status" className="flex flex-wrap gap-2">
        {(['alle', ...FILTER_ORDER] as const).map((key) => {
          const count = key === 'alle' ? rows.length : counts[key]
          if (key !== 'alle' && count === 0 && filter !== key) return null
          const active = filter === key
          return (
            <Button
              key={key}
              type="button"
              size="sm"
              variant={active ? 'default' : 'outline'}
              aria-pressed={active}
              onClick={() => {
                setFilter(key)
                setPage(1)
              }}
            >
              {`${FILTER_LABELS[key]} (${count})`}
            </Button>
          )
        })}
      </div>
      {props.panel}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={selected.size === 0}
          onClick={() => {
            props.onSkip([...selected])
            setSelected(new Set())
          }}
        >
          Overslaan selectie{selected.size > 0 ? ` (${selected.size})` : ''}
        </Button>
        {open > 0 ? (
          <Button type="button" size="sm" variant="outline" onClick={props.onAllNew}>
            Alle mogelijk dubbel: toch nieuw
          </Button>
        ) : null}
      </div>
      <div ref={scroller} className="max-h-[calc(100svh-8rem)] overflow-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-semibold text-muted-foreground">
              <th scope="col" className={`${HEAD} pr-2`}>
                <input
                  type="checkbox"
                  checked={allShownSelected}
                  onChange={(event) => toggleShown(event.target.checked)}
                  aria-label="Selecteer alle rijen op deze pagina"
                />
              </th>
              <th scope="col" className={`${HEAD} pr-2`}>
                Status
              </th>
              <th scope="col" className={`${HEAD} pr-2`}>
                {lead.label}
              </th>
              <th scope="col" className={`${HEAD} pr-2`}>
                Actie
              </th>
              {others.map((column) => (
                <th key={column.field} scope="col" className={`${HEAD} pr-2`}>
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((index) => (
              <PreviewRow
                key={index}
                mode={mode}
                input={inputs[index]}
                row={rows[index]}
                choice={choices[index]}
                lead={lead}
                others={others}
                selected={selected.has(index)}
                targetSkipped={targetSkipped(rows[index])}
                onSelect={(on) => toggle(index, on)}
                onEdit={(field, value) => props.onEdit(index, field, value)}
                onLeave={() => props.onLeave(index)}
                onChoice={(choice) => props.onChoice(index, choice)}
              />
            ))}
          </tbody>
        </table>
      </div>
      {visible.length > PAGE_SIZE ? (
        <Pagination page={page} pageSize={PAGE_SIZE} total={visible.length} onPage={setPage} />
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={props.onBack}>
            Terug
          </Button>
          <Button type="button" variant="outline" onClick={props.onRecheckAll} disabled={checking}>
            Opnieuw controleren
          </Button>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex flex-wrap items-center gap-3">
            {open > 0 ? (
              <p className="text-sm text-warning">
                Kies bij {open} mogelijk dubbele {open === 1 ? 'rij' : 'rijen'} wat er moet gebeuren.
              </p>
            ) : null}
            <Button type="button" onClick={props.onCommit} disabled={!canCommit}>
              {committing ? 'Bezig met importeren…' : `${planned.created} importeren · ${planned.known} aanvullen`}
            </Button>
          </div>
          {props.listName ? <p className="text-sm text-foreground">{`De contacten uit dit bestand komen ook op de lijst '${props.listName}'.`}</p> : null}
          <p className="text-xs text-muted-foreground">
            Aanvullingen van bestaande {noun} worden bij ongedaan maken niet teruggedraaid.
          </p>
        </div>
      </div>
    </section>
  )
}
